import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { formatApiError } from "../../lib/api";
import { hostAddStudent } from "../../lib/schools";
import { buildClassAdmissionWhatsAppMessage } from "../../lib/schoolFeeMessages";
import ContactPhoneField from "../ContactPhoneField";
import FormTextField from "../FormTextField";
import FormLabel from "../FormLabel";
import { ChoiceToggleGroup, FieldHint } from "../createEvent/CreateEventFormUi";
import { ReadableNavButton } from "../Sticker";
import {
  WorkflowModalShell,
  WorkflowModalHeader,
  WorkflowModalBody,
  WorkflowModalFooter,
} from "../workflow/WorkflowModal";
import WhatsAppFollowUpPrompt from "../WhatsAppFollowUpPrompt";

const ENTRY_TYPES = [
  { k: "Drop-in", label: "Drop-in" },
  { k: "Credit", label: "Credit" },
];

const ROLES = [
  { k: "Leader", label: "Leader" },
  { k: "Follower", label: "Follower" },
];

export default function AddWalkInModal({
  instanceId,
  sessionLabel,
  className,
  schoolName = "",
  onClose,
  onDone,
}) {
  const dialogRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    role: "Leader",
    entryType: "Drop-in",
  });
  const [addedStudent, setAddedStudent] = useState(null);

  function update(k, v) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  async function submit() {
    if (!form.name.trim()) {
      toast.error("Name is required.");
      return;
    }
    if (!form.role) {
      toast.error("Choose a role.");
      return;
    }
    setLoading(true);
    try {
      const noteParts = [`Entry: ${form.entryType}`];
      if (form.email.trim()) noteParts.push(`Email: ${form.email.trim()}`);
      const rsvp = await hostAddStudent(instanceId, {
        name: form.name.trim(),
        phone: form.phone.trim() || undefined,
        role: form.role,
        note: noteParts.join(" · "),
      });
      toast.success("Walk-in added");
      onDone?.(rsvp);
      setAddedStudent({
        name: rsvp?.name || form.name.trim(),
        phone: rsvp?.phone || form.phone.trim(),
      });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }

  if (addedStudent) {
    return (
      <WhatsAppFollowUpPrompt
        title="Walk-in added"
        description={`Send ${addedStudent.name || "the student"} their class confirmation on WhatsApp.`}
        phone={addedStudent.phone}
        message={buildClassAdmissionWhatsAppMessage({
          studentName: addedStudent.name,
          className,
          sessionLabel,
          schoolName,
        })}
        onClose={onClose}
        testId="walk-in-admission-whatsapp"
      />
    );
  }

  return (
    <WorkflowModalShell
      dialogRef={dialogRef}
      onClose={onClose}
      onBackdropClose={onClose}
      testId="class-dash-walk-in-modal"
      titleId="class-dash-walk-in-title"
    >
      <WorkflowModalHeader
        eyebrow={className || "Class"}
        title="Add Walk-in Student"
        titleId="class-dash-walk-in-title"
        onClose={onClose}
        closeTestId="class-dash-walk-in-close"
        sticky
      />

      <WorkflowModalBody className="space-y-4">
        {sessionLabel ? (
          <p className="font-ui text-[13px] text-ink/70 -mt-1">{sessionLabel}</p>
        ) : null}
        <FieldHint>
          For walk-ins at the door. Doesn&apos;t use class credits from a student account.
        </FieldHint>
        <FormTextField
          label="Name"
          required
          value={form.name}
          onChange={(e) => update("name", e.target.value)}
          placeholder="Student name"
          data-testid="class-dash-walk-in-name"
        />
        <FormTextField
          label="Email"
          type="email"
          value={form.email}
          onChange={(e) => update("email", e.target.value)}
          placeholder="Optional"
          data-testid="class-dash-walk-in-email"
        />
        <ContactPhoneField
          value={form.phone}
          onChange={(phone) => update("phone", phone)}
          showHint={false}
          optional
          placeholder="Optional"
          testId="class-dash-walk-in-phone"
        />
        <fieldset className="min-w-0 border-0 p-0 m-0">
          <FormLabel as="legend" className="block mb-2">Role *</FormLabel>
          <ChoiceToggleGroup
            options={ROLES}
            value={form.role}
            onChange={(role) => update("role", role)}
            testIdPrefix="class-dash-walk-in-role"
          />
        </fieldset>
        <fieldset className="min-w-0 border-0 p-0 m-0">
          <FormLabel as="legend" className="block mb-2">Class Entry Type</FormLabel>
          <ChoiceToggleGroup
            options={ENTRY_TYPES}
            value={form.entryType}
            onChange={(entryType) => update("entryType", entryType)}
            testIdPrefix="class-dash-walk-in-entry"
          />
        </fieldset>
      </WorkflowModalBody>

      <WorkflowModalFooter sticky safeArea>
        <ReadableNavButton
          type="button"
          variant="primary"
          className="w-full py-3"
          disabled={loading || !form.name.trim()}
          onClick={submit}
          data-testid="class-dash-walk-in-submit"
        >
          {loading ? "Saving…" : "Add Walk-in →"}
        </ReadableNavButton>
      </WorkflowModalFooter>
    </WorkflowModalShell>
  );
}
